import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, ShieldAlert, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/validation-email`;
const money = (v) => `${Math.round(v || 0).toLocaleString("fr-FR")} FCFA`;

function Summary({ d }) {
  const rows = [["Référence", d.numero], ["Opération", `${d.op_label} ${d.source_ref}`], ["Objet", d.objet], ["Demandeur", d.demandeur_name],
    ...(d.montant != null ? [["Montant", money(d.montant)]] : []), ["Étape", d.etape], ["Validateur", d.validateur]];
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm bg-white rounded-xl p-4 border" data-testid="vemail-summary">
      {rows.map(([k, v]) => [<dt key={`k${k}`} className="text-slate-500">{k}</dt>, <dd key={`v${k}`} className="font-medium break-words">{v || "—"}</dd>])}
    </dl>
  );
}

function Done({ ok, title, text }) {
  const Icon = ok ? CheckCircle2 : ShieldAlert;
  return (
    <div className={`rounded-2xl border-2 p-5 text-center ${ok ? "border-emerald-400 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="vemail-result">
      <Icon className={`h-12 w-12 mx-auto ${ok ? "text-emerald-600" : "text-amber-600"}`} /><p className="font-head text-lg font-bold mt-2">{title}</p><p className="text-sm text-slate-600">{text}</p>
    </div>
  );
}

export default function ValidateByEmail() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [action, setAction] = useState(sp.get("action") === "reject" ? "reject" : "approve");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${API}/${token}`).then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Lien invalide")); }, [token]);
  const submit = async () => {
    setBusy(true); setErr("");
    try { const r = await axios.post(`${API}/${token}`, { action, comment }); setDone(r.data); }
    catch (e) { setErr(e.response?.data?.detail || "Décision impossible"); } finally { setBusy(false); }
  };
  const reject = action === "reject";
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8" data-testid="vemail-page">
      <div className="max-w-md mx-auto space-y-4">
        <p className="text-[11px] font-mono uppercase tracking-widest text-brand-700 text-center">OMNIBAT ONE · Validation</p>
        {!d && !err && <p className="text-center text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Chargement…</p>}
        {!d && err && <Done ok={false} title="Lien invalide" text={err} />}
        {done && <Done ok title={reject ? "Rejet enregistré" : "Approbation enregistrée"} text={`Demande ${done.numero} — statut : ${done.statut}. Vous pouvez fermer cette page.`} />}
        {d && !done && <>
          <h1 className="font-head text-2xl font-bold text-center">Confirmer votre décision</h1>
          <Summary d={d} />
          {!d.valid ? <Done ok={false} title="Décision impossible par ce lien" text={d.reason} /> : <>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" disabled={!d.can_approve} onClick={() => setAction("approve")} className={`rounded-xl border-2 p-3 text-sm font-semibold disabled:opacity-40 ${!reject ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "bg-white"}`} data-testid="vemail-choose-approve"><CheckCircle2 className="h-5 w-5 mx-auto mb-1" />Approuver</button>
              <button type="button" disabled={!d.can_reject} onClick={() => setAction("reject")} className={`rounded-xl border-2 p-3 text-sm font-semibold disabled:opacity-40 ${reject ? "border-rose-500 bg-rose-50 text-rose-800" : "bg-white"}`} data-testid="vemail-choose-reject"><XCircle className="h-5 w-5 mx-auto mb-1" />Rejeter</button>
            </div>
            <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={reject ? "Motif du rejet (obligatoire)" : "Commentaire (facultatif)"} className="w-full rounded-xl border p-3 text-sm" data-testid="vemail-comment" />
            {err && <p className="text-sm text-rose-600" data-testid="vemail-error">{err}</p>}
            <button type="button" disabled={busy || (reject && !comment.trim()) || (reject ? !d.can_reject : !d.can_approve)} onClick={submit}
              className={`w-full rounded-xl py-3 text-white font-semibold disabled:opacity-50 ${reject ? "bg-rose-600" : "bg-[#076633]"}`} data-testid="vemail-confirm">
              {busy ? "Enregistrement…" : reject ? "Confirmer le rejet" : "Confirmer l'approbation"}</button>
          </>}
        </>}
        <p className="text-[11px] text-slate-400 text-center">Lien personnel et à usage unique. Votre décision est enregistrée à votre nom avec la mention « via e-mail ».</p>
      </div>
    </div>
  );
}
